import React from 'react';
import * as DrawerPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button, ButtonVariant } from 'mmds';
import cn from 'clsx';
import s from './Drawer.module.css';
import { DialogTitle } from '@radix-ui/react-dialog';

export type Side = 'top' | 'right' | 'bottom' | 'left';

interface DrawerPanelProps
    extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
    inFrom?: Side;
    className?: string;
    variant?: string;
}

interface DrawerCloseButtonProps extends React.HTMLAttributes<HTMLDivElement> {
    closeButtonVariant?: ButtonVariant;
}

interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    closeButtonVariant?: ButtonVariant;
    variant?: string;
}

const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Overlay
        className={cn(s.overlay, className)}
        {...props}
        ref={ref}
    />
));

const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(
    ({ children, className, closeButtonVariant, variant, ...props }, ref) => {
        return (
            <div className={cn(s.header, className)} ref={ref} {...props}>
                <div className={cn(s.headerInner)}>{children}</div>
                {variant !== 'product-assistance' && (
                    <DrawerCloseButton
                        closeButtonVariant={closeButtonVariant}
                    />
                )}
            </div>
        );
    }
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerBody = ({
    children,
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn(s.body, className)} {...props}>
        {children}
    </div>
);
DrawerBody.displayName = 'DrawerBody';

const DrawerFooter = ({
    children,
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn(s.footer, className)} {...props}>
        <div className={cn(s.footerInner, className)}>{children}</div>
    </div>
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerPortal = ({ ...props }) => <DrawerPrimitive.Portal {...props} />;

const Drawer = ({ children, open, onOpenChange, ...props }) => (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange} {...props}>
        {children}
    </DrawerPrimitive.Root>
);

const DrawerCloseButton = ({ closeButtonVariant }: DrawerCloseButtonProps) => {
    return (
        <DrawerPrimitive.Close className={s.close} asChild>
            <Button
                icon="close"
                variant={closeButtonVariant || 'secondary'}
                isIconNoMargin={true}
            >
                <span className="visually-hidden">Close</span>
            </Button>
        </DrawerPrimitive.Close>
    );
};
DrawerCloseButton.displayName = 'DrawerCloseButton';

const DrawerPanel = React.forwardRef<HTMLDivElement, DrawerPanelProps>(
    ({ inFrom, variant, className, children, ...props }, ref) => {
        const contentClassName = cn(
            s.root,
            {
                [s.top]: inFrom === 'top',
                [s.right]: inFrom === 'right',
                [s.bottom]: inFrom === 'bottom',
                [s.left]: inFrom === 'left',
                [s.productAssistance]: variant === 'product-assistance',
                [s.lookbook]: variant === 'lookbook',
                [s.filters]: variant === 'filters',
                [s.catalogueNav]: variant === 'catalogue-nav',
                [s.catalogueNavDesktop]: variant === 'catalogue-nav-desktop',
                [s.cartNav]: variant === 'cart-nav',
                [s.searchPanel]: variant === 'search-panel',
                [s.trueFit]: variant === 'truefit',
            },
            className
        );

        return (
            <DrawerPortal>
                <DrawerOverlay />
                <DrawerPrimitive.Content
                    className={contentClassName}
                    {...props}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    ref={ref}
                >
                    <VisuallyHidden>
                        <DialogTitle>MAAP UI Sidebar {variant}</DialogTitle>
                    </VisuallyHidden>
                    {children}
                </DrawerPrimitive.Content>
            </DrawerPortal>
        );
    }
);

export {
    Drawer,
    DrawerTrigger,
    DrawerClose,
    DrawerPanel,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerCloseButton,
};
